import React, { useCallback, useEffect, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, FormControl, MenuItem, Select, SxProps, Theme, Typography } from '@mui/material';

import { GUIDE_TEXT_MAX_WIDTH } from './guidePage.constants';
import { SectionHeader } from './SectionHeader';

export type GuidePageSection = Readonly<{
  id: string;
  title: string;
  subtitle?: string;
  /** Rendered to the right of the section title. */
  rightSlot?: React.ReactNode;
  content: React.ReactNode;
}>;

type GuideSectionsProps = Readonly<{
  header: Readonly<{ title: string; description: readonly string[] }>;
  /** The overview block's anchor id, plus an optional slot rendered inside it. */
  overview: Readonly<{ id: string; extra?: React.ReactNode }>;
  sections: readonly GuidePageSection[];
}>;

/**
 * Guide body laid out as a list of titled sections, introduced by the guide's overview copy
 * and navigable through a jump-to-section list that tracks the section in view.
 */
export const GuideSections: React.FC<GuideSectionsProps> = ({ header, overview, sections }) => {
  const [activeSection, setActiveSection] = useState<string>(overview.id);

  const navItems: readonly { id: string; label: string }[] = [
    { id: overview.id, label: appStrings.modules.guidePage.overview },
    ...sections.map(({ id, title }) => ({ id, label: title })),
  ];

  const sectionIdsKey = navItems.map((item) => item.id).join('|');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const topmostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (topmostVisible) {
          setActiveSection(topmostVisible.target.id);
        }
      },
      { rootMargin: '-30% 0px -60% 0px' },
    );

    sectionIdsKey.split('|').forEach((id) => {
      const el = document.getElementById(id);

      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sectionIdsKey]);

  const handleNavClick = useCallback((id: string) => {
    setActiveSection(id);
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <Box sx={rootSx}>
      <Box id={overview.id} sx={overviewSx}>
        {/* Mobile: section nav dropdown */}
        <Box sx={sectionNavMobileSx}>
          <FormControl fullWidth size="small">
            <Select<string>
              id="guide-page-section-nav-select"
              value={activeSection}
              onChange={(e) => handleNavClick(e.target.value)}
              sx={sectionNavSelectSx}
            >
              {navItems.map((item) => (
                <MenuItem key={item.id} id={`guide-page-nav-item-${item.id}`} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={summaryBodySx}>
          <Box sx={summaryContentSx}>
            <Typography variant="display-md-semibold" sx={heroTitleSx}>
              {header.title}
            </Typography>
            {header.description.map((paragraph) => (
              <Typography key={paragraph} variant="text-md-regular">
                {paragraph}
              </Typography>
            ))}
          </Box>

          {/* Desktop: vertical jump-to-section nav */}
          <Box sx={sectionNavDesktopSx}>
            <Box sx={sectionNavCardSx}>
              <Box sx={sectionNavTitleSx}>
                <Typography variant="text-sm-semibold">
                  {appStrings.modules.guidePage.jumpToSection}
                </Typography>
              </Box>
              {navItems.map((item) => {
                const isActive = item.id === activeSection;

                return (
                  <Box
                    key={item.id}
                    id={`guide-page-nav-${item.id}`}
                    onClick={() => handleNavClick(item.id)}
                    sx={sectionNavItemSx(isActive)}
                  >
                    <Typography variant={isActive ? 'text-md-semibold' : 'text-md-regular'}>
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {overview.extra}
      </Box>

      {sections.map((section) => (
        <Box key={section.id} id={section.id}>
          <SectionHeader
            title={section.title}
            subtitle={section.subtitle}
            rightSlot={section.rightSlot}
          />
          {section.content}
        </Box>
      ))}
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-6xl'],
  width: '100%',
});

const overviewSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const summaryBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  pt: backrs.spacing['spacing-3xl'],
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    gap: backrs.spacing['spacing-xl'],
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  '@container (min-width: 900px)': {
    gap: backrs.spacing['spacing-3xl'],
  },
});

const summaryContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  flex: 1,
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const heroTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  mb: backrs.spacing['spacing-md'],
});

const sectionNavDesktopSx: SxProps<Theme> = {
  display: 'none',
  '@container (min-width: 600px)': {
    display: 'block',
    flexShrink: 0,
    width: '200px',
  },
  '@container (min-width: 700px)': {
    width: '280px',
  },
};

const sectionNavCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  p: backrs.spacing['spacing-md'],
  gap: backrs.spacing['spacing-xxs'],
});

const sectionNavTitleSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-sm'],
  pb: backrs.spacing['spacing-xs'],
  color: backrs.colors['Colors/Text/text-brand-primary'],
});

const sectionNavMobileSx: SxProps<Theme> = {
  display: 'none',
  '@container (max-width: 599px)': {
    display: 'block',
    width: '100%',
  },
};

const sectionNavSelectSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  width: '100%',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    py: backrs.spacing['spacing-md'],
  },
  '& .MuiSelect-icon': {
    display: 'block !important',
    color: backrs.colors['Colors/Text/text-primary'],
  },
});

const sectionNavItemSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    px: backrs.spacing['spacing-lg'],
    py: backrs.spacing['spacing-md'],
    cursor: 'pointer',
    borderRadius: backrs.radius['radius-md'],
    color: isActive
      ? backrs.colors['Colors/Text/text-primary']
      : backrs.colors['Colors/Text/text-secondary'],
    outline: isActive ? `1px solid ${backrs.colors['Colors/Base/Black']}` : '1px solid transparent',
    outlineOffset: '-1px',
    '&:hover': {
      color: backrs.colors['Colors/Text/text-primary'],
      outline: `2px solid ${backrs.colors['Colors/Border/border-brand']}`,
      outlineOffset: '-1px',
    },
  });
